import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { confirmSession } from '../../utils/stripe';
import VideoCall from './VideoCall';
import './ProviderSessions.css';

const ProviderSessions = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, in_progress, completed
  const [activeVideoCall, setActiveVideoCall] = useState(null);
  const [confirmingSession, setConfirmingSession] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchSessions();
    }
  }, [currentUser]);


  const fetchSessions = async () => {
    try {
      setLoading(true);
      
      // Fetch all sessions where current user is the provider
      const sessionsQuery = query(
        collection(db, 'sessions'),
        where('providerId', '==', currentUser.uid)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      const sessionsData = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Check for expired sessions and update their status
      const updatedSessions = await Promise.all(
        sessionsData.map(async (session) => {
          if (shouldMarkAsExpired(session)) {
            try {
              await updateDoc(doc(db, 'sessions', session.id), {
                status: 'expired',
                updatedAt: serverTimestamp()
              });
              return { ...session, status: 'expired' };
            } catch (error) {
              console.error('Error updating expired session:', error);
              return session;
            }
          }
          return session;
        })
      );
      
      // Sort by creation date
      updatedSessions.sort((a, b) => {
        const aTime = a.createdAt?.seconds || Date.now() / 1000;
        const bTime = b.createdAt?.seconds || Date.now() / 1000;
        return bTime - aTime;
      });
      
      setSessions(updatedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSession = async (sessionId) => {
    try {
      setConfirmingSession(sessionId);
      
      // Call Firebase function to confirm session and send email
      await confirmSession(sessionId);

      // Update local state
      setSessions(prev => prev.map(session => 
        session.id === sessionId 
          ? { ...session, status: 'confirmed', confirmedAt: new Date() }
          : session
      ));
    } catch (error) {
      console.error('Error confirming session:', error);
      alert('Error confirming session. Please try again.');
    } finally {
      setConfirmingSession(null);
    }
  };


  const handleJoinVideoCall = (session) => {
    setActiveVideoCall(session);
  };

  const handleEndVideoCall = async (sessionId, callDuration) => {
    setActiveVideoCall(null);
    // Optionally update session with actual call duration
    try {
      await updateDoc(doc(db, 'sessions', sessionId), {
        actualDuration: callDuration,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating session duration:', error);
    }
  };


  const getFilteredSessions = () => {
    if (filter === 'all') return sessions;
    return sessions.filter(session => session.status === filter);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#f59e0b';
      case 'confirmed': return '#3b82f6';
      case 'in_progress': return '#059669';
      case 'completed': return '#6b7280';
      case 'cancelled': return '#dc2626';
      case 'expired': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'scheduled': return 'Scheduled';
      case 'confirmed': return 'Confirmed';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      case 'expired': return 'Expired';
      default: return status;
    }
  };

  const canJoinSession = (session) => {
    // Only confirmed or in_progress sessions can be joined
    if (!['confirmed', 'in_progress'].includes(session.status)) {
      return false;
    }

    // Check if session is within 15 minutes of scheduled time
    const sessionDateTime = new Date(`${session.scheduledDate}T${session.scheduledTime}`);
    const now = new Date();
    const fifteenMinutesBefore = new Date(sessionDateTime.getTime() - (15 * 60 * 1000));
    const fifteenMinutesAfter = new Date(sessionDateTime.getTime() + (15 * 60 * 1000));
    
    return now >= fifteenMinutesBefore && now <= fifteenMinutesAfter;
  };

  const isSessionExpired = (session) => {
    const sessionDateTime = new Date(`${session.scheduledDate}T${session.scheduledTime}`);
    const now = new Date();
    const fifteenMinutesAfter = new Date(sessionDateTime.getTime() + (15 * 60 * 1000));
    
    return now > fifteenMinutesAfter;
  };

  const shouldMarkAsExpired = (session) => {
    // Only mark sessions as expired if they are 'confirmed' or 'in_progress' and past the window
    return ['confirmed', 'in_progress'].includes(session.status) && isSessionExpired(session);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  const formatScheduledDateTime = (date, time) => {
    if (!date || !time) return 'Not scheduled';
    
    // Convert 24-hour time to 12-hour format with AM/PM
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    const formattedTime = `${hour12}:${minutes} ${ampm}`;
    
    return `${new Date(date).toLocaleDateString()} at ${formattedTime}`;
  };

  // Show video call interface if active
  if (activeVideoCall) {
    return (
      <VideoCall
        sessionId={activeVideoCall.id}
        userId={currentUser.uid}
        isProvider={true}
        onCallEnd={(duration) => handleEndVideoCall(activeVideoCall.id, duration)}
      />
    );
  }

  if (loading) {
    return <div className="loading">Loading sessions...</div>;
  }

  return (
    <div className="provider-sessions">
      <div className="sessions-header">
        <h2>My Provider Sessions</h2>
        <p className="sessions-tagline">Manage your session bookings and confirmations</p>
      </div>

      {/* Filter Controls */}
      <div className="sessions-filters">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Sessions</option>
            <option value="scheduled">Scheduled</option>
            <option value="confirmed">Confirmed</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Sessions List */}
      <div className="sessions-list">
        {getFilteredSessions().length === 0 ? (
          <div className="no-sessions">
            <div className="no-sessions-icon">📅</div>
            <h3>No Sessions Found</h3>
            <p>
              {filter === 'all' 
                ? 'You don\'t have any session bookings yet.'
                : `No sessions with status "${getStatusLabel(filter)}" found.`
              }
            </p>
          </div>
        ) : (
          getFilteredSessions().map(session => (
            <div key={session.id} className="session-card">
              <div className="session-header">
                <div className="session-info">
                  <h3>{session.sessionType}</h3>
                </div>
                <div className="session-status-area">
                  <div 
                    className="session-status"
                    style={{ backgroundColor: getStatusColor(session.status) }}
                  >
                    {getStatusLabel(session.status)}
                  </div>
                  <div className="session-schedule">
                    {formatScheduledDateTime(session.scheduledDate, session.scheduledTime)}
                  </div>
                </div>
              </div>

              <div className="session-details">
                <div className="session-details-layout">
                  <div className="details-column">
                    <div className="detail-item">
                      <span className="detail-label">Practitioner:</span>
                      <span className="detail-value">{session.practitionerName}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Duration:</span>
                      <span className="detail-value">{session.duration} minutes</span>
                    </div>
                  </div>
                  <div className="details-column">
                    <div className="detail-item">
                      <span className="detail-label">Amount:</span>
                      <span className="detail-value">{formatCurrency(session.amount)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Booked:</span>
                      <span className="detail-value">{formatDate(session.createdAt)}</span>
                    </div>
                  </div>
                  {session.notes && (
                    <div className="detail-item detail-notes">
                      <span className="detail-label">Notes:</span>
                      <span className="detail-value">{session.notes}</span>
                    </div>
                  )}
                  {session.transfer && (
                    <div className="detail-item detail-transfer">
                      <span className="detail-label">Transfer:</span>
                      <span className="detail-value">
                        {formatCurrency(session.transfer.amount / 100)} transferred 
                        (Fee: {formatCurrency(session.transfer.platformFee / 100)})
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="session-actions">
                <div className="action-buttons">
                  {session.status === 'scheduled' && (
                    <div className="scheduled-actions">
                      <div className="status-info">
                        <span className="waiting-text">
                          📅 Session scheduled - awaiting confirmation
                        </span>
                      </div>
                      <button 
                        onClick={() => handleConfirmSession(session.id)}
                        disabled={confirmingSession === session.id}
                        className="btn-confirm"
                      >
                        {confirmingSession === session.id ? 'Confirming...' : 'Confirm Session'}
                      </button>
                    </div>
                  )}
                  
                  {session.status === 'confirmed' && (
                    <div className="confirmed-actions">
                      <div className="status-info">
                        <span className={canJoinSession(session) ? "ready-text" : isSessionExpired(session) ? "error-text" : "waiting-text"}>
                          {canJoinSession(session) 
                            ? "✅ Session confirmed - ready to start"
                            : isSessionExpired(session)
                            ? "⏰ Session window expired - please contact practitioner"
                            : "⏱️ Session confirmed - join window opens 15 min before scheduled time"
                          }
                        </span>
                      </div>
                      {canJoinSession(session) && (
                        <button 
                          onClick={() => handleJoinVideoCall(session)}
                          className="btn-video-call"
                        >
                          📹 Join Video Call
                        </button>
                      )}
                    </div>
                  )}
                  
                  {session.status === 'in_progress' && (
                    <div className="in-progress-actions">
                      <div className="status-info">
                        <span className={canJoinSession(session) ? "active-text" : "error-text"}>
                          {canJoinSession(session) 
                            ? "🟢 Session in progress"
                            : "⏰ Session window expired - please contact practitioner"
                          }
                        </span>
                      </div>
                      {canJoinSession(session) && (
                        <button 
                          onClick={() => handleJoinVideoCall(session)}
                          className="btn-video-call"
                        >
                          📹 Join Video Call
                        </button>
                      )}
                    </div>
                  )}

                  {session.status === 'completed' && (
                    <div className="completed-badge">
                      ✅ Session Completed
                    </div>
                  )}

                  {session.status === 'expired' && (
                    <div className="status-info">
                      <span className="error-text">
                        ⏰ Session Expired - Window Closed
                      </span>
                    </div>
                  )}

                  {session.status === 'cancelled' && (
                    <div className="status-info">
                      <span className="error-text">
                        ❌ Session Cancelled
                      </span>
                    </div>
                  )}
                </div>

                <button 
                  onClick={() => navigate(`/session/${session.id}`)}
                  className="btn-view-details"
                >
                  📋 View Details & Messages
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProviderSessions;